import { Controller } from '@hotwired/stimulus';

// Address lookup for recycling convert flow.
// Enter a valid UK postcode, click Find (or press Enter) → addresses appear as a list → select to populate.
const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;

export default class extends Controller {
  static targets = ['input', 'findBtn', 'dropdown', 'selected', 'line1', 'line2', 'city', 'county', 'postcode', 'manual', 'confirmation', 'postageOptions', 'summaryAddress'];

  connect() {
    this._outsideClick = (e) => {
      if (!this.element.contains(e.target)) this.hideDropdown();
    };
    document.addEventListener('click', this._outsideClick);
    this.toggleFindBtn();
  }

  disconnect() {
    document.removeEventListener('click', this._outsideClick);
  }

  // Called on every keyup — enable/disable Find button based on valid postcode
  onInput() {
    this.toggleFindBtn();
  }

  // Called on Enter key in the input
  onKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.isValidPostcode()) this.find();
    }
  }

  // Called on Find button click
  find() {
    const postcode = this.inputTarget.value.trim();
    if (!this.isValidPostcode()) return;
    this.fetchAddresses(postcode);
  }

  toggleFindBtn() {
    if (this.hasFindBtnTarget) {
      this.findBtnTarget.disabled = !this.isValidPostcode();
    }
  }

  isValidPostcode() {
    return UK_POSTCODE_RE.test(this.inputTarget.value.trim());
  }

  async fetchAddresses(postcode) {
    if (this.hasFindBtnTarget) {
      this.findBtnTarget.disabled = true;
      this.findBtnTarget.textContent = 'Searching...';
    }

    const pathParts = window.location.pathname.split('/steps/');
    const url = pathParts[0] + '/steps/address_lookup/postcode_lookup';
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ postcode })
      });

      const data = await response.json();
      this.renderDropdown(data.addresses || []);
    } catch (e) {
      this.renderDropdown([]);
    } finally {
      if (this.hasFindBtnTarget) {
        this.findBtnTarget.textContent = 'Find';
        this.toggleFindBtn();
      }
    }
  }

  renderDropdown(addresses) {
    const dropdown = this.dropdownTarget;
    dropdown.innerHTML = '';

    if (addresses.length === 0) {
      dropdown.innerHTML = '<div class="al-item al-empty">No addresses found for this postcode</div>';
      dropdown.style.display = 'block';
      return;
    }

    addresses.forEach((addr) => {
      const item = document.createElement('div');
      item.className = 'al-item';
      item.textContent = addr.address;
      item.addEventListener('click', () => this.pickAddress(addr));
      dropdown.appendChild(item);
    });

    dropdown.style.display = 'block';
  }

  pickAddress(addr) {
    this.hideDropdown();

    // Parse: "57 Hubert Grove, London, Greater London, SW9 9NY"
    const parts = addr.address.split(',').map(s => s.trim());
    const line1 = parts[0] || '';
    const city = parts.length >= 3 ? parts[1] : '';
    const county = parts.length >= 4 ? parts[2] : '';
    const postcode = addr.postcode || (parts.length > 0 ? parts[parts.length - 1] : '');

    if (this.hasLine1Target) this.line1Target.value = line1;
    if (this.hasLine2Target) this.line2Target.value = '';
    if (this.hasCityTarget) this.cityTarget.value = city;
    if (this.hasCountyTarget) this.countyTarget.value = county;
    if (this.hasPostcodeTarget) this.postcodeTarget.value = postcode;
    if (this.hasSelectedTarget) this.selectedTarget.value = postcode;

    if (this.hasConfirmationTarget) {
      this.confirmationTarget.textContent = addr.address;
      this.confirmationTarget.style.display = 'block';
    }

    this.inputTarget.value = postcode;

    this.showPostageOptions();
    this.saveAddress({ address: addr.address, postcode, line_1: line1, city, county });
  }

  hideDropdown() {
    if (this.hasDropdownTarget) this.dropdownTarget.style.display = 'none';
  }

  showManual(event) {
    event.preventDefault();
    if (this.hasManualTarget) this.manualTarget.style.display = '';
    this.showPostageOptions();
  }

  showPostageOptions() {
    if (this.hasPostageOptionsTarget) {
      this.postageOptionsTargets.forEach(el => el.style.display = '');
    }
  }

  async saveAddress(addressData) {
    const pathParts = window.location.pathname.split('/steps/');
    const url = pathParts[0] + '/steps/address_lookup/address_selected';
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify(addressData)
      });

      const data = await response.json();
      if (this.hasSummaryAddressTarget && data.address) {
        this.summaryAddressTarget.textContent = data.address;
        this.summaryAddressTarget.closest('.summary-address-row').style.display = '';
      }
    } catch (e) {
      // Address will still be saved on form submit as fallback
    }
  }
}
